import {useCallback, useEffect, useRef, useState} from 'react';
import axios from "axios";
import {
    Contextable,
    SessionConnectHandler,
    SessionDisconnectHandler,
    SessionHook,
    SessionMessageHandler,
} from './webSocketTypes';

import * as url from "../urls";
import {getLoggedinUser} from "../apiCore";
import {useRedux} from "../../hooks";
import {setWSConnected, setWSDisconnected} from "../../redux/Main/Login/actions";

export function useSession(
    onOpen: SessionConnectHandler,
    onMessage: SessionMessageHandler,
    onClose: SessionDisconnectHandler
): SessionHook {

    const [session, setSession] = useState(null as unknown as EventSource);
    // The one stream this hook owns. A dropped stream makes the app join() again while the
    // browser keeps retrying the old EventSource on its own, so connect() must close it:
    // otherwise every drop leaves one more open stream, and over HTTP/1.1 (yarn start on
    // localhost) six of them take every connection the origin has and all requests hang.
    const current = useRef<EventSource | null>(null);
    const { dispatch } = useRedux();

    const updateOpenHandler = () => {
        if (!session) return;
        session.addEventListener('open', onOpen);
        return () => {
            session.removeEventListener('open', onOpen);
        };
    };

    const updateMessageHandler = () => {
        if (!session) return;
        session.addEventListener('message', onMessage);
        return () => {
            session.removeEventListener('message', onMessage);
        };
    };

    // EventSource has no 'close' event; a dropped/failed connection surfaces as 'error'
    // (the browser then auto-reconnects, which re-fires 'open' on success).
    const updateCloseHandler = () => {
        if (!session) return;
        session.addEventListener('error', onClose);
        return () => {
            session.removeEventListener('error', onClose);
        };
    };

    useEffect(updateOpenHandler, [session, onOpen]);
    useEffect(updateMessageHandler, [session, onMessage]);
    useEffect(updateCloseHandler, [session, onClose]);

    // A token request in flight: joining again meanwhile must not start a second stream.
    const connecting = useRef(false);

    const connect = useCallback(
        () => {
            const loggedInUser = getLoggedinUser();
            const token = loggedInUser?.token;
            const userUuid = loggedInUser?.uuid;

            if (!token || !userUuid) return;

            // A stream that is open, or that the browser is still (re)connecting, is kept:
            // replacing it is what looped (each new stream closed the one before it, ~4/s).
            if (connecting.current) return;
            if (current.current && current.current.readyState !== EventSource.CLOSED) return;
            connecting.current = true;

            // Ask the API to issue the secure HttpOnly `mercureAuthorization` cookie
            // (JWT-authenticated request), then open the SSE subscription. The cookie
            // is scoped to the API origin — the same origin as the hub — so
            // `withCredentials` lets EventSource authorize against Mercure.
            axios.get(url.GET_MERCURE_TOKEN, { withCredentials: true })
                .then(() => {
                    const hubUrl = new URL(url.MERCURE_HUB);
                    hubUrl.searchParams.append('topic', '/users/' + userUuid);

                    current.current?.close();
                    const es = new EventSource(hubUrl.toString(), { withCredentials: true });
                    current.current = es;

                    // The store follows the stream's own events, not a render-time readyState:
                    // a fresh stream is CONNECTING, which read as "disconnected" and made the
                    // app join again. An error while the browser retries on its own is not a
                    // disconnect either; only a stream it gave up on (CLOSED) is.
                    es.addEventListener('open', () => {
                        if (current.current === es) dispatch(setWSConnected());
                    });
                    es.addEventListener('error', () => {
                        if (current.current === es && es.readyState === EventSource.CLOSED) {
                            dispatch(setWSDisconnected());
                        }
                    });

                    setSession(es);
                })
                .catch(() => {
                    dispatch(setWSDisconnected());
                })
                .finally(() => {
                    connecting.current = false;
                });
        },
        [dispatch]
    );

    // Mercure subscriptions are receive-only; publishing happens via the REST API.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const sendMessage = <T extends Contextable>(args: T) => {
        // no-op
    };

    const close = useCallback(() => {
        current.current?.close();
    }, []);

    useEffect(() => {
        const owned = current;
        return () => owned.current?.close();
    }, []);

    return [connect, sendMessage, close];
}
